import React from "react";
import {
	Button,
	ChildrenProps,
	ClassNameProps,
	Column,
	Conditional,
	Else,
	If,
	isArray,
	makeClassName,
	Row,
	StyleProps,
	TruncatedText,
} from "@hex-insights/core";
import { Badge } from "../../../../Components/Badge";
import { Icon } from "../../../../Components/Icon";
import {
	MedicalCondition,
	MedicalConditionNameFragment,
	Medication,
	MedicationNameFragment,
	Person,
	PersonNameAndImageFragment,
	Student,
	StudentMedicalCondition,
	StudentOTCMedication,
	StudentPrescriptionMedication,
	StudentVaccinationRecord,
	Vaccine,
	VaccineNameFragment,
} from "../../../../Utilities";
import { MedicalConditionFormat, MedicationFormat, PersonFormat, StudentFormat, VaccineFormat } from "../../../Format";
import { StudentMedicalConditionUtilities } from "../../../Utilities";
import { StudentIcon } from "../Icon";
import styles from "./styles.module.css";

export type StudentHealthReviewCardProps = {
	student: Pick<Student, "id"> & {
		person: Pick<Person, "id" | "dateOfBirth"> & PersonNameAndImageFragment;
		studentMedicalConditions: (Pick<StudentMedicalCondition, "id" | "notes"> & {
			medicalCondition: Pick<MedicalCondition, "id" | "conditionType"> & MedicalConditionNameFragment;
		})[];
		studentPrescriptionMedications: (Pick<
			StudentPrescriptionMedication,
			"id" | "administration" | "notes" | "canSelfAdminister"
		> & {
			medication: Pick<Medication, "id"> & MedicationNameFragment;
		})[];
		studentOtcMedications: (Pick<StudentOTCMedication, "id" | "isAllowed" | "notes"> & {
			medication: Pick<Medication, "id"> & MedicationNameFragment;
		})[];
		studentVaccinationRecords: (Pick<StudentVaccinationRecord, "id" | "receivedOn" | "notes"> & {
			vaccine: Pick<Vaccine, "id"> & VaccineNameFragment;
		})[];
	};
	onEditClick?: () => void;
} & Partial<ClassNameProps & StyleProps>;

export function StudentHealthReviewCard({ student, onEditClick, className, style }: StudentHealthReviewCardProps) {
	const { allergies, foodIntolerances, medicalConditions } = React.useMemo(
		() => StudentMedicalConditionUtilities.splitByConditionType(student.studentMedicalConditions),
		[student.studentMedicalConditions],
	);

	return (
		<Column
			justify="spaced-start"
			verticalSpacing="1rem"
			className={makeClassName(styles["card"], className)}
			style={style}
		>
			<Row justify="spaced-start" horizontalSpacing="0.5rem">
				<Row justify="spaced-start" align="center" horizontalSpacing="1rem" style={{ flexGrow: 1, minWidth: 0 }}>
					<StudentIcon student={student} size={64} />

					<Column justify="spaced-start" verticalSpacing="0.25rem" style={{ minWidth: 0 }}>
						<TruncatedText className={styles["card__name"]} style={{ minWidth: 0 }}>
							{StudentFormat.name(student)}
						</TruncatedText>
						<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem">
							<Icon.PersonArmsSpread size={16} color="var(--neutral-colors--tertiary)" />
							<TruncatedText className={styles["card__detail__text"]}>
								{PersonFormat.age(student.person.dateOfBirth)}
							</TruncatedText>
						</Row>
					</Column>
				</Row>

				{onEditClick && (
					<Button variant="link" size="small" onClick={onEditClick} className="button--icon">
						<Icon.PencilSimple size={24} className="block" />
					</Button>
				)}
			</Row>

			<HealthSection label="Allergies">
				{allergies.map((e) => (
					<HealthBadge key={e.id}>{MedicalConditionFormat.name(e.medicalCondition)}</HealthBadge>
				))}
			</HealthSection>

			<HealthSection label="Food Intolerances">
				{foodIntolerances.map((e) => (
					<HealthBadge key={e.id}>{MedicalConditionFormat.name(e.medicalCondition)}</HealthBadge>
				))}
			</HealthSection>

			<HealthSection label="Medical Conditions">
				{medicalConditions.map((e) => (
					<HealthBadge key={e.id}>{MedicalConditionFormat.name(e.medicalCondition)}</HealthBadge>
				))}
			</HealthSection>

			<HealthSection label="OTC Medications">
				{student.studentOtcMedications.map((e) => (
					<OTCMedicationBadge key={e.id} isAllowed={e.isAllowed}>
						{MedicationFormat.name(e.medication)}
					</OTCMedicationBadge>
				))}
			</HealthSection>

			<HealthSection label="Prescription Medications">
				{student.studentPrescriptionMedications.map((e) => (
					<HealthBadge key={e.id}>{MedicationFormat.name(e.medication)}</HealthBadge>
				))}
			</HealthSection>

			<HealthSection label="Vaccinations">
				{student.studentVaccinationRecords.map((e) => (
					<HealthBadge key={e.id}>{VaccineFormat.name(e.vaccine)}</HealthBadge>
				))}
			</HealthSection>
		</Column>
	);
}

type HealthSectionProps = {
	label: string;
} & Partial<ChildrenProps>;

function HealthSection({ label, children }: HealthSectionProps) {
	return (
		<Column justify="spaced-start" verticalSpacing="0.25rem">
			<span className={styles["card__label"]}>{label}</span>
			<Conditional>
				<If condition={isArray(children) ? children.length > 0 : !!children}>
					<Row
						justify="spaced-start"
						overflow="wrap"
						withVerticalSpacing
						horizontalSpacing="0.25rem"
						verticalSpacing="0.25rem"
					>
						{children}
					</Row>
				</If>
				<Else>
					<span className={styles["card__section__none"]}>None</span>
				</Else>
			</Conditional>
		</Column>
	);
}

type HealthBadgeProps = Partial<ChildrenProps>;

function HealthBadge({ children }: HealthBadgeProps) {
	return (
		<Badge
			color="var(--brand-colors--primary--dark)"
			backgroundColor="#e8f4fe"
			style={{ border: "1.5px solid rgba(23, 144, 235, 0.15)", maxWidth: "100%" }}
		>
			<TruncatedText>{children}</TruncatedText>
		</Badge>
	);
}

type OTCMedicationBadgeProps = {
	isAllowed: boolean;
} & Partial<ChildrenProps>;

function OTCMedicationBadge({ isAllowed, children }: OTCMedicationBadgeProps) {
	const IconComponent = isAllowed ? Icon.Check : Icon.X;

	return (
		<Badge
			color={isAllowed ? "var(--brand-colors--primary--dark)" : "var(--theme-colors__pink--dark)"}
			backgroundColor={isAllowed ? "#e8f4fe" : "var(--theme-colors__pink--background)"}
			style={{
				border: "1.5px solid " + (isAllowed ? "rgba(23, 144, 235, 0.15)" : "var(--theme-colors__pink--light)"),
				maxWidth: "100%",
			}}
		>
			<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
				<IconComponent size={14} />
				<TruncatedText>{children}</TruncatedText>
			</Row>
		</Badge>
	);
}
